/**
 * Admin Authentication Hook
 * 
 * Handles wallet-signature-based authentication for admin endpoints.
 * Uses SIWE-style signed messages with JWT session tokens.
 */

import { useState, useCallback, useEffect } from 'react';
import { useAccount, useSignMessage } from 'wagmi';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_TOKEN_KEY = 'adminToken';
const ADMIN_SESSION_KEY = 'adminSession';

// Treasury wallet address (admin wallet)
const TREASURY_ADDRESS = '0xcAD86262Df9fe4333cD923f7321757E910A0447F'.toLowerCase();

export function useAdminAuth() {
  const { address, isConnected } = useAccount();
  const { signMessageAsync } = useSignMessage();
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionInfo, setSessionInfo] = useState(null);

  // Check if connected wallet is admin wallet
  const isAdminWallet = isConnected && address?.toLowerCase() === TREASURY_ADDRESS;

  // Get stored token
  const getStoredToken = useCallback(() => {
    return sessionStorage.getItem(ADMIN_TOKEN_KEY);
  }, []);

  // Get stored session info
  const getStoredSession = useCallback(() => {
    const session = sessionStorage.getItem(ADMIN_SESSION_KEY);
    return session ? JSON.parse(session) : null;
  }, []);

  // Clear auth state
  const clearAuth = useCallback(() => {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    setIsAuthenticated(false);
    setSessionInfo(null);
  }, []);

  // Verify existing token
  const verifyToken = useCallback(async () => {
    const token = getStoredToken();
    if (!token) {
      setIsAuthenticated(false);
      return false;
    }

    try {
      const response = await axios.get(`${API_URL}/api/admin/auth/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.authenticated) {
        setIsAuthenticated(true);
        setSessionInfo(getStoredSession());
        return true;
      }
    } catch (err) {
      // Token invalid or expired
      clearAuth();
    }
    
    return false;
  }, [getStoredToken, getStoredSession, clearAuth]);

  // Login with wallet signature
  const login = useCallback(async () => {
    if (!isAdminWallet) {
      setError('Please connect with the admin wallet');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Step 1: Request challenge
      const challengeResponse = await axios.post(`${API_URL}/api/admin/auth/challenge`, {
        wallet: address
      });

      const { message, nonce } = challengeResponse.data;

      // Step 2: Sign the message
      const signature = await signMessageAsync({ message });

      // Step 3: Submit signature for verification
      const loginResponse = await axios.post(`${API_URL}/api/admin/auth/login`, {
        wallet: address,
        signature,
        nonce
      });

      const { token, sessionId, expiresAt } = loginResponse.data;

      // Step 4: Store token and session info
      sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
      sessionStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify({
        sessionId,
        wallet: address,
        expiresAt
      }));

      setIsAuthenticated(true);
      setSessionInfo({ sessionId, wallet: address, expiresAt });
      
      return true;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Login failed';
      setError(errorMessage);
      clearAuth();
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [address, isAdminWallet, signMessageAsync, clearAuth]);

  // Logout
  const logout = useCallback(async () => {
    const token = getStoredToken();
    
    if (token) {
      try {
        await axios.post(`${API_URL}/api/admin/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        // Ignore logout errors - clear local state anyway
        console.warn('Logout request failed:', err);
      }
    }

    clearAuth();
  }, [getStoredToken, clearAuth]);

  // Create axios instance with auth header
  const getAuthHeaders = useCallback(() => {
    const token = getStoredToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getStoredToken]);

  // Authenticated API call helper
  const authFetch = useCallback(async (url, options = {}) => {
    const token = getStoredToken();
    
    if (!token) {
      throw new Error('Not authenticated');
    }

    const config = {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`
      }
    };

    try {
      return await axios(url, config);
    } catch (err) {
      if (err.response?.status === 401) {
        // Token expired or invalid
        clearAuth();
        throw new Error('Session expired. Please login again.');
      }
      throw err;
    }
  }, [getStoredToken, clearAuth]);

  // Check auth status on mount and when wallet changes
  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  // Clear auth if wallet disconnects or changes
  useEffect(() => {
    if (!isConnected || !isAdminWallet) {
      const session = getStoredSession();
      if (session && session.wallet?.toLowerCase() !== address?.toLowerCase()) {
        clearAuth();
      }
    }
  }, [isConnected, isAdminWallet, address, getStoredSession, clearAuth]);

  // Calculate time remaining
  const getTimeRemaining = useCallback(() => {
    if (!sessionInfo?.expiresAt) return null;
    
    const expiresAt = new Date(sessionInfo.expiresAt);
    const now = new Date();
    const diffMs = expiresAt - now;
    
    if (diffMs <= 0) return 'Expired';
    
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const remainingMins = diffMins % 60;
    
    if (diffHours > 0) {
      return `${diffHours}h ${remainingMins}m`;
    }
    return `${diffMins}m`;
  }, [sessionInfo]);

  return {
    // State
    isAuthenticated,
    isLoading,
    error,
    sessionInfo,
    
    // Wallet info
    isConnected,
    isAdminWallet,
    walletAddress: address,
    
    // Actions
    login,
    logout,
    verifyToken,
    clearError: () => setError(null),
    
    // Helpers
    getAuthHeaders,
    authFetch,
    getTimeRemaining,
    getStoredToken
  };
}

export default useAdminAuth;
